import { useCallback, useEffect, useMemo, useState } from "react";

import {
  getJudoTechniquesWithoutSelfDefense,
  getTechniquesJudo,
} from "../../../../services/techniquesServices";
import { buildQuestions } from "../utils/quizUtils";

const STORAGE_KEY = "monappli:techniques-quizz:preferences";

function readStoredPreferences() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);

    if (Array.isArray(parsed?.selectedGrades)) {
      return parsed;
    }

    // Compatibilité avec l’ancienne version qui stockait un grade unique.
    if (
      parsed?.grade &&
      parsed.grade !== "Toutes les ceintures" &&
      typeof parsed.grade === "string"
    ) {
      return {
        ...parsed,
        selectedGrades: [parsed.grade],
      };
    }

    return {
      ...parsed,
      selectedGrades: [],
    };
  } catch {
    return null;
  }
}

function saveStoredPreferences(nextPreferences) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextPreferences));
  } catch {
    // localStorage peut être indisponible selon le navigateur.
  }
}

function getGradeLabel(selectedGrades = []) {
  if (!selectedGrades.length) return "Toutes les ceintures";
  if (selectedGrades.length === 1) return selectedGrades[0];
  return `${selectedGrades.length} ceintures`;
}

export function useTechniquesQuizController({ questionCount = 10 } = {}) {
  const storedPreferences = useMemo(() => readStoredPreferences(), []);

  const [techniques, setTechniques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedGrades, setSelectedGrades] = useState(
    Array.isArray(storedPreferences?.selectedGrades)
      ? storedPreferences.selectedGrades
      : []
  );

  const [includeSelfDefense, setIncludeSelfDefense] = useState(
    Boolean(storedPreferences?.includeSelfDefense)
  );

  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const [errors, setErrors] = useState([]);
  const [finished, setFinished] = useState(false);

  const gradeLabel = useMemo(
    () => getGradeLabel(selectedGrades),
    [selectedGrades]
  );

  useEffect(() => {
    saveStoredPreferences({
      selectedGrades,
      includeSelfDefense,
    });
  }, [selectedGrades, includeSelfDefense]);

  const resetQuizWithQuestions = useCallback((nextQuestions) => {
    setQuestions(nextQuestions);
    setCurrent(0);
    setSelected(null);
    setScore(0);
    setErrors([]);
    setFinished(false);
  }, []);

  const generateQuestions = useCallback(
    (sourceTechniques = techniques) => {
      const nextQuestions = buildQuestions(sourceTechniques, {
        selectedGrades,
        count: questionCount,
      });

      resetQuizWithQuestions(nextQuestions);
    },
    [questionCount, resetQuizWithQuestions, selectedGrades, techniques]
  );

  const fetchTechniques = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const data = includeSelfDefense
        ? await getTechniquesJudo()
        : await getJudoTechniquesWithoutSelfDefense();

      setTechniques(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erreur lors du chargement des techniques :", err);

      setTechniques([]);
      resetQuizWithQuestions([]);

      setError(
        "Les techniques n’ont pas pu être récupérées. Vérifie la connexion ou le service techniques."
      );
    } finally {
      setLoading(false);
    }
  }, [includeSelfDefense, resetQuizWithQuestions]);

  useEffect(() => {
    fetchTechniques();
  }, [fetchTechniques]);

  useEffect(() => {
    if (loading || error) return;

    if (!techniques.length) {
      resetQuizWithQuestions([]);
      return;
    }

    generateQuestions(techniques);
  }, [
    error,
    generateQuestions,
    loading,
    resetQuizWithQuestions,
    techniques,
  ]);

  const currentQuestion = questions[current] || null;

  const progress = useMemo(() => {
    const total = Math.max(questions.length, 1);
    const answeredCount = Math.min(current + (selected ? 1 : 0), total);
    return (answeredCount / total) * 100;
  }, [current, questions.length, selected]);

  const toggleGrade = useCallback((grade) => {
    setSelectedGrades((previousGrades) => {
      if (previousGrades.includes(grade)) {
        return previousGrades.filter((item) => item !== grade);
      }

      return [...previousGrades, grade];
    });
  }, []);

  const clearSelectedGrades = useCallback(() => {
    setSelectedGrades([]);
  }, []);

  const onAnswer = useCallback(
    (option) => {
      if (selected || finished || !currentQuestion) return;

      setSelected(option);

      if (option === currentQuestion.correctAnswer) {
        setScore((value) => value + 1);
        return;
      }

      setErrors((previousErrors) => [
        ...previousErrors,
        {
          questionId: currentQuestion.id,
          question: currentQuestion,
          selected: option,
        },
      ]);
    },
    [currentQuestion, finished, selected]
  );

  const nextQuestion = useCallback(() => {
    if (!selected) return;

    if (current >= questions.length - 1) {
      setFinished(true);
      return;
    }

    setCurrent((value) => value + 1);
    setSelected(null);
  }, [current, questions.length, selected]);

  const restart = useCallback(() => {
    generateQuestions(techniques);
  }, [generateQuestions, techniques]);

  const toggleSelfDefense = useCallback(() => {
    setIncludeSelfDefense((value) => !value);
  }, []);

  return {
    techniques,
    loading,
    error,

    selectedGrades,
    gradeLabel,
    toggleGrade,
    clearSelectedGrades,

    includeSelfDefense,
    toggleSelfDefense,

    questions,
    current,
    selected,
    score,
    errors,
    finished,
    progress,
    currentQuestion,

    availableTechniquesCount: techniques.length,

    onAnswer,
    nextQuestion,
    restart,
    refreshTechniques: fetchTechniques,
  };
}